import { motion } from 'framer-motion';
import { useState } from 'react';
import { Sparkles, Send } from 'lucide-react';

const QuizSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    window.open('https://gillesdelieuze.com/#quiz-form', '_blank');
    setIsSubmitting(false);
  };

  return (
    <section
      id="quiz"
      data-testid="quiz-section"
      className="relative py-24 md:py-32 bg-[#F5F5F4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side - Nouveau design moderne */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative order-2 md:order-1"
          >
            <div className="relative">
              {/* Cadre dore decoratif */}
              <div className="absolute -inset-4 border border-[#D4AF37]/30 rounded-3xl" />
              <div className="absolute -inset-8 border border-[#D4AF37]/10 rounded-3xl" />
              
              {/* Image principale moderne */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1756672987836-4749b7c0270c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxkb2xwaGluJTIwb2NlYW4lMjBzdW5saWdodCUyMGJlYXV0aWZ1bCUyMHBlYWNlZnVsJTIwYmx1ZXxlbnwwfHx8fDE3NzM4NDM3MDh8MA&ixlib=rb-4.1.0&q=85"
                  alt="Dauphin lumineux dans l'océan"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                  data-testid="quiz-image"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09]/80 via-transparent to-transparent" />
                
                {/* Texte overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-2">
                    Quiz Gratuit
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                    Quel Dauphin<br />
                    <span className="italic text-[#D4AF37]">te guide</span> en ce moment ?
                  </h3>
                </div>
              </div>
              
              {/* Badge flottant */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-4 -right-4 bg-[#D4AF37] text-[#1C1917] rounded-full px-4 py-2 shadow-lg"
              >
                <span className="font-bold text-sm uppercase tracking-wider">2 min</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Form Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-1 md:order-2"
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="text-[#D4AF37]" size={20} />
              <span className="text-[#D4AF37] uppercase tracking-widest text-sm font-medium">
                Découvrez votre guide
              </span>
            </div>

            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#1C1917] leading-tight mb-6">
              Quel <span className="italic text-[#D4AF37]">Dauphin Allié</span><br />
              t'accompagne ?
            </h2>

            <p className="text-[#44403C] text-lg leading-relaxed mb-8">
              Un dauphin veille sur toi, prêt à t'accompagner dans ta transformation.
              Il t'aide à dépasser tes défis, activer tes potentiels et harmoniser
              ton énergie pour franchir ton prochain palier d'évolution.
            </p>

            <div className="space-y-3 mb-8 text-[#44403C]">
              <p className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                Fais le quiz en 2 minutes
              </p>
              <p className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                Reçois ton guide personnalisé
              </p>
              <p className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                Découvre ton Dauphin allié
              </p>
            </div>

            {/* Form Card */}
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-[#D4AF37]/10">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="quiz-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Prénom"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
                      required
                      data-testid="quiz-input-firstname"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Nom"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
                      required
                      data-testid="quiz-input-lastname"
                    />
                  </div>
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Adresse e-mail"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
                    required
                    data-testid="quiz-input-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="quiz-submit-btn"
                  className="w-full mt-4 bg-gradient-to-r from-[#D4AF37] to-[#AA8C2C] text-white rounded-full px-8 py-4 uppercase tracking-widest text-sm font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? 'Envoi...' : 'Je fais le Quiz'}
                  <Send size={16} />
                </button>
                <p className="text-xs text-[#A8A29E] text-center mt-3">
                  Tu recevras en prime mes articles et pépites hebdomadaires
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QuizSection;
